import type * as React from "react";
import { useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDetect } from "../hooks/useDetect";
import { useReadFile } from "../hooks/useReadFile";
import { PageContainer } from "./custom/PageContainer";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { InputForm } from "./custom/InputForm";
import { usePlagiarism } from "../hooks/usePlagiarism";
import type { ISO639_1_Language } from "@shared/types";
import { useDetectLanguage } from "../hooks/useDetectLanguage";
import { useDefaultUserLanguage } from "../hooks/useDefaultUserLanguage";
import { useProcessSelectedText } from "../hooks/useProcessSelectedText";

const maxTextLength = 10000;
const minTextLength = 400;
const maxPlagiarismTextLength = 5000;
const minPlagiarismTextLength = 300;

interface RootProps {
	selectedText?: string | null;
}

const Root: React.FC<RootProps> = ({ selectedText }: RootProps) => {
	const [text, setText] = useState("");
	const [plagiarismText, setPlagiarismText] = useState("");
	const navigate = useNavigate();
	const location = useLocation();
	const initialTab =
		location.pathname === "/plagiarism" ? "plagiarism" : "detect";
	const [tabValue, setTabValue] = useState(initialTab);
	const { detectAI, isLoading } = useDetect();
	const { checkPlagiarism, isLoading: isPlagiarismLoading } = usePlagiarism();
	const { readTextFile, isLoading: isReading } = useReadFile();

	const defaultLanguage = useDefaultUserLanguage();
	const [language, setLanguage] = useState<ISO639_1_Language>(defaultLanguage);
	const [plagiarismLanguage, setPlagiarismLanguage] =
		useState<ISO639_1_Language>(defaultLanguage);
	const detectLanguage = useDetectLanguage(setLanguage);
	const detectPlagiarismLanguage = useDetectLanguage(setPlagiarismLanguage);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const updateUIState = (text: string) => {
		setText(text);
		detectLanguage(text);

		if (fileInputRef.current) {
			fileInputRef.current.value = ""; // clears the file input
		}
	};

	useProcessSelectedText(updateUIState, selectedText);

	const handleTextSubmit = async () => {
		if (!text.trim()) return;

		detectAI(text, language).then(() => {
			navigate("/result_detector");
		});
	};

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			readTextFile(file).then((text) => {
				setText(text);
				detectLanguage(text);
			});
		}
	};

	const handlePlagiarismFileUpload = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (file) {
			readTextFile(file, maxPlagiarismTextLength).then((text) => {
				setPlagiarismText(text);
				detectPlagiarismLanguage(text);
			});
		}
	};

	const handlePlagiarismTextSubmit = async () => {
		if (!plagiarismText.trim()) return;

		checkPlagiarism({
			text: plagiarismText,
			language: plagiarismLanguage,
		}).then(() => {
			navigate("/result_plagiarism");
		});
	};

	const handleTabChange = (value: "detect" | "plagiarism") => {
		setTabValue(value);
		if (value === "plagiarism") {
			navigate("/plagiarism");
		} else {
			navigate("/");
		}
	};

	return (
		<PageContainer>
			<Tabs
				value={tabValue}
				onValueChange={(value) =>
					handleTabChange(value as "detect" | "plagiarism")
				}
				defaultValue="detect"
				className="h-full flex flex-col min-h-0"
			>
				<TabsList className="mx-3 mt-3 flex-shrink-0">
					<TabsTrigger
						value="detect"
						disabled={isLoading || isPlagiarismLoading}
					>
						Detect AI
					</TabsTrigger>
					<TabsTrigger
						value="plagiarism"
						disabled={isLoading || isPlagiarismLoading}
					>
						Check Plagiarism
					</TabsTrigger>
				</TabsList>
				<TabsContent value="detect" className="flex-1 min-h-0">
					<InputForm
						actionTitle="Check for AI Content"
						text={text}
						isLoading={isLoading}
						isReadingFile={isReading}
						language={language}
						fileInputRef={fileInputRef}
						minTextLength={minTextLength}
						maxTextLength={maxTextLength}
						warningText="AI detection is based on statistical models and isn’t 100% accurate. Use results as guidance, not proof."
						onLanguageChange={setLanguage}
						onTextChange={setText}
						onFileChange={handleFileUpload}
						onPressSubmit={handleTextSubmit}
					/>
				</TabsContent>
				<TabsContent value="plagiarism" className="flex-1 min-h-0">
					<InputForm
						actionTitle="Check Plagiarism"
						text={plagiarismText}
						isLoading={isPlagiarismLoading}
						isReadingFile={isReading}
						language={plagiarismLanguage}
						minTextLength={minPlagiarismTextLength}
						maxTextLength={maxPlagiarismTextLength}
						warningText="Plagiarism detection compares text against existing sources and isn’t 100% accurate. Use results as guidance, not proof."
						onLanguageChange={setPlagiarismLanguage}
						onTextChange={setPlagiarismText}
						onFileChange={handlePlagiarismFileUpload}
						onPressSubmit={handlePlagiarismTextSubmit}
					/>
				</TabsContent>
			</Tabs>
		</PageContainer>
	);
};

export default Root;
